import type { AvailableSitesResponse } from '@atlassiansox/cross-flow-plugins';
import { getApiGatewayUrl } from '@trello/api-gateway';
import { Analytics } from '@trello/atlassian-analytics';
import { clientVersion } from '@trello/config';
import { sendErrorEvent } from '@trello/error-reporting';
import { TrelloStorage } from '@trello/storage';

import { getAvailableSitesStorageKey } from './getAvailableSitesStorageKey';
import { getCachedAvailableSites } from './getCachedAvailableSites';

export const getSwitcherAvailableProductsUrl = () =>
  getApiGatewayUrl('/available-products/api/available-products');

// The available-products endpoint will always return an array containing at least the Trello "site".
// To check if a user has access to an Atlassian product, it should be filtered out from getAvailableSites()
// (eg. sites?.filter(({ cloudId }) => cloudId !== 'TRELLO'))

export const getAvailableSites = async (): Promise<AvailableSitesResponse> => {
  try {
    const cachedAvailableSites = getCachedAvailableSites();
    if (cachedAvailableSites !== null) {
      return Promise.resolve({ sites: cachedAvailableSites });
    }
    const url = getSwitcherAvailableProductsUrl();
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    });
    if (response.ok) {
      Analytics.sendOperationalEvent({
        action: 'fetched',
        actionSubject: 'fetchAvailableSites',
        source: 'crossFlowEssentials',
      });
      const resolvedReponse = await response.json();
      TrelloStorage.set(getAvailableSitesStorageKey(), {
        timestamp: Date.now(),
        data: resolvedReponse,
      });
      return Promise.resolve(resolvedReponse);
    }
    if (response.status === 403) {
      // we expect this is if the CST indicates the user hasn't verified yet
      const resolvedReponse = await response.json();
      if (
        resolvedReponse.message ===
        'User has not verified its email, operation is not allowed'
      ) {
        Analytics.sendOperationalEvent({
          action: 'errored',
          actionSubject: 'fetchAvailableSites',
          source: 'crossFlowEssentials',
          attributes: {
            unverified: true,
          },
        });
        return { sites: [] };
      }
    }
    if (response.status === 401) {
      // we expect this is if the user's Aa is not valid
      Analytics.sendOperationalEvent({
        action: 'errored',
        actionSubject: 'fetchAvailableSites',
        source: 'crossFlowEssentials',
      });
      return { sites: [] };
    }
    throw new Error('unauthorized');
  } catch (error) {
    Analytics.sendOperationalEvent({
      action: 'errored',
      actionSubject: 'fetchAvailableSites',
      source: 'crossFlowEssentials',
    });
    sendErrorEvent(error, {
      tags: {
        ownershipArea: 'trello-cross-flow-b',
        feature: 'Atlassian Switcher',
      },
    });
    return { sites: [] };
  }
};
